import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

const NikeConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [sessionId] = useState(() => `nike-${Date.now()}`);

  // Calculate total credit from empties
  const totalCredit = state.empties.reduce((sum, item) => {
    const dollarValue = item.product.rewards?.dollarValue;
    if (dollarValue) {
      return sum + parseFloat(dollarValue.replace('$', ''));
    }
    return sum;
  }, 0);

  // Get products list from empties for display
  const products = state.empties.map(item => item.product);

  // Celebration effect and send completion event on mount
  useEffect(() => {
    // Send completion event to PIC API
    const sendCompletionEvent = async () => {
      try {
        await fetch('https://agentic-commerce.pentatonic.com/api/v1/pin/events', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': 'nike',
          },
          body: JSON.stringify({
            sessionId,
            eventType: 'trade_in_completed',
            eventData: {
              creditValue: totalCredit,
              itemCount: products.length,
              productNames: products.map(p => p.name),
              store: 'Nike House of Innovation NYC',
            },
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (error) {
        console.log('Event tracking skipped (demo mode)');
      }
    };

    sendCompletionEvent();

    // Store session ID for potential future reference
    localStorage.setItem('nike_last_session', sessionId);
  }, [sessionId, totalCredit, products]);

  const handleDone = () => {
    dispatch({ type: 'RESET' });
    navigate('/nike');
  };

  const handleNewTradeIn = () => {
    dispatch({ type: 'RESET' });
    navigate('/nike/takeback');
  };

  const steps = [
    {
      title: 'Quality Check',
      description: 'Our team inspects and grades your shoes',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Clean & Certify',
      description: 'Shoes are professionally cleaned and certified',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    },
    {
      title: 'New Home',
      description: 'Someone else gets to enjoy your shoes',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-center">
        <svg className="h-8 w-auto text-white" viewBox="0 0 69 32" fill="currentColor">
          <path d="M68.56 4.05c-.4-.15-5.86-.95-9.84 2.01-3.98 2.96-17.14 17.37-17.14 17.37s-12.87-8.36-13.93-8.97c-1.06-.61-6.85-3.29-11.11 1.21-4.26 4.5-12.15 12.67-12.15 12.67s-.55.54-.08.8c.47.26 1.09-.01 1.09-.01s20.22-8.5 22.01-9.32c1.79-.82 6.13-2.51 10.18 1.06 4.05 3.57 15.89 13.32 15.89 13.32s3.21 2.72 6.23.86c3.02-1.86 9.1-6.04 9.1-6.04s.87-.69.17-1.26c-.7-.57-1.43.11-1.43.11s-7.66 4.4-9.52 5.32c-1.86.92-4.21 1.1-7.04-1.47-2.83-2.57-13.97-12.2-13.97-12.2s10.66-11.77 13.46-14.33c2.8-2.56 7.69-4.4 12.89-.47 0 0 3.54 2.69 4.25 3.17.71.48 1.62.59 1.62-.28 0-.87-.68-1.55-.68-1.55z"/>
        </svg>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
          className="w-24 h-24 bg-[#FA5400] rounded-full flex items-center justify-center mb-6"
        >
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-3">
            Trade-In Complete!
          </h1>
          <p className="text-lg text-white/60">
            Thank you for being part of Nike Refurbished
          </p>
        </motion.div>

        {/* Receipt Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full mb-8 shadow-2xl"
        >
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
            <div>
              <p className="text-gray-500 text-sm">Items Accepted</p>
              <p className="text-3xl font-bold text-[#111111]">{products.length}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-500 text-sm">Credit Earned</p>
              <p className="text-3xl font-bold text-[#FA5400]">${totalCredit.toFixed(2)}</p>
            </div>
          </div>

          {/* Items list */}
          <div className="space-y-3 mb-6">
            {products.map((product, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{product.name}</span>
                <span className="font-medium text-[#111111]">
                  {product.rewards?.dollarValue || '$0.00'}
                </span>
              </div>
            ))}
          </div>

          {/* Session ID */}
          <div className="text-center pt-4 border-t border-gray-100">
            <p className="text-gray-400 text-xs">Confirmation #{sessionId}</p>
          </div>
        </motion.div>

        {/* What Happens Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-2xl w-full mb-8"
        >
          <h3 className="text-white font-bold text-center mb-6">What Happens Next</h3>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.title}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-[#FA5400] mb-3">
                    {step.icon}
                  </div>
                  <h4 className="text-white font-medium text-sm mb-1">{step.title}</h4>
                  <p className="text-white/50 text-xs max-w-[120px]">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-px bg-white/20 mx-4 mt-[-24px]" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex gap-4"
        >
          <button
            onClick={handleNewTradeIn}
            className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
          >
            Trade In More
          </button>
          <button
            onClick={handleDone}
            className="px-8 py-3 bg-white text-[#111111] rounded-full font-bold hover:bg-gray-100 transition-colors"
          >
            Done
          </button>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10">
        <p>Your credit has been added to your Nike account</p>
      </footer>
    </div>
  );
};

export default NikeConfirmationView;
